// ReferralBookings.js — COMPLETE (All Filters Fixed + Cursor Fixed + Date Range Popup Redesigned)
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FaUserMd, FaUserInjured, FaSearch, FaCalendarAlt, FaClock,
  FaTimes, FaPhoneAlt, FaRupeeSign, FaCheckCircle,
  FaTimesCircle, FaEye, FaEdit, FaHandHoldingUsd,
  FaBuilding,
  FaPills, FaFlask, FaClinicMedical, FaUser, FaServicestack,
  FaChevronLeft, FaChevronRight, FaMoneyBillWave, FaCreditCard,
  FaMobileAlt, FaUniversity, FaWallet, FaWalking, FaGlobe,
} from "react-icons/fa";
import {
  FiUsers, FiUserCheck, FiClock, FiFilter, FiDownload, FiTrash2,
  FiEye, FiEdit2, FiRefreshCw, FiCheckCircle, FiXCircle,
  FiChevronDown, FiChevronUp, FiAward, FiDollarSign
} from "react-icons/fi";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "https://api.timelyhealth.in/api";
const REFERRAL_API = `${API_BASE_URL}/referralcontacts`;
const BOOKINGS_API = `${API_BASE_URL}/appointment-slots`;

const TIME_FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisWeek", label: "This Week" },
  { value: "last7Days", label: "Last 7 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "thisYear", label: "This Year" },
  { value: "All", label: "All" },
];

const PAYMENT_TYPE_FILTER_OPTIONS = [
  { value: "All", label: "All Payment Modes" },
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online" },
  { value: "wallet", label: "Wallet" },
];

const BOOKING_TYPE_OPTIONS = [
  { value: "All", label: "All Booking Types" },
  { value: "Walk-In", label: "Walk-In" },
  { value: "Online", label: "Online" },
];

const REVENUE_CATEGORY_OPTIONS = [
  { value: "All", label: "All Revenue Types" },
  { value: "service", label: "Service Only" },
  { value: "lab", label: "Lab Only" },
  { value: "pharmacy", label: "Pharmacy Only" },
  { value: "fees", label: "Fees Only" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "cash", label: "Cash", icon: FaMoneyBillWave, color: "emerald" },
  { value: "online", label: "Online", icon: FaMobileAlt, color: "cyan" },
  { value: "wallet", label: "Wallet", icon: FaWallet, color: "amber" },
];

const CATEGORY_META = {
  service: { label: "Service", icon: FaServicestack, bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  pharmacy: { label: "Pharmacy", icon: FaPills, bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  lab: { label: "Lab", icon: FaFlask, bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  fees: { label: "Fees", icon: FaMoneyBillWave, bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
};

// ==================== HELPERS ====================
const extractId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object" && val._id) return String(val._id);
  return "";
};

const extractName = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") return val.customerName || val.doctorName || val.name || "";
  return "";
};

const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${yy}`;
  } catch { return "N/A"; }
};

const formatDateTimeToDDMMYYYY = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${yy} ${hh}:${mm}`;
  } catch { return "N/A"; }
};

const getPaymentStatusColors = (status) => {
  const normalized = status === "Due" ? "Pending" : status;
  const map = {
    Paid: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: FaCheckCircle, iconColor: "text-emerald-600" },
    Partial: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: FaClock, iconColor: "text-amber-600" },
    Pending: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: FaTimesCircle, iconColor: "text-red-500" },
  };
  return map[normalized] || map.Pending;
};

const getBookingType = (booking) => {
  if (!booking) return { label: "Walk-In", icon: FaWalking, color: "bg-amber-50 text-amber-700 border-amber-200" };
  if (booking.bookingType === "N-Walkin") {
    return { label: "N-Walkin", icon: FaWalking, color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  if (booking.bookingType === "R-Walkin") {
    return { label: "R-Walkin", icon: FaWalking, color: "bg-blue-50 text-blue-700 border-blue-200" };
  }
  if (booking.isOP === true) return { label: "Walk-In", icon: FaWalking, color: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Online", icon: FaGlobe, color: "bg-cyan-50 text-cyan-700 border-cyan-200" };
};

const getSlotTiming = (booking) => {
  if (!booking) return "-";
  const st = booking.startTime || booking.slotDetails?.startTime || "";
  const et = booking.endTime || booking.slotDetails?.endTime || "";
  return st && et ? `${st} - ${et}` : "-";
};

const getBookingServices = (booking) => {
  if (!booking) return [];
  const fromServiceItems = Array.isArray(booking.serviceItems) && booking.serviceItems.length > 0 ? booking.serviceItems : null;
  const fromServices = Array.isArray(booking.services) && booking.services.length > 0 ? booking.services : null;
  const arr = fromServiceItems || fromServices || [];
  return arr.map((s) => {
    const raw = s.paymentStatus || booking.paymentStatus || "Pending";
    return {
      serviceId: s.serviceId || s._id || "",
      _id: s.serviceId || s._id || "",
      name: s.name || "Service",
      price: Number(s.price) || 0,
      description: s.description || "",
      paymentStatus: raw === "Due" ? "Pending" : raw,
      category: s.category || s.serviceCategory || s.type || "",
      serviceCategory: s.serviceCategory || "",
    };
  });
};

const getBookingFinalPayable = (booking) => {
  if (!booking) return 0;
  const final =
    Number(booking.finalPayable) || Number(booking.finalPayableAmount) ||
    Number(booking.grandTotal) || Number(booking.totalAmount) || 0;
  if (final > 0) return final;
  const items = getBookingServices(booking);
  const subtotal = items.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
  const commission = Number(booking.commissionAmount) || 0;
  const discount = Number(booking.discount) || 0;
  return subtotal - commission - discount;
};

const getBookingPaidInfo = (booking) => {
  if (!booking) return { final: 0, paid: 0, balance: 0, status: "Pending" };
  const final = getBookingFinalPayable(booking);
  const raw = booking.paymentStatus || "Pending";
  const status = raw === "Due" ? "Pending" : raw;
  let paid = Number(booking.amountPaid) || 0;
  if (status === "Paid") paid = final;
  else if (status === "Pending") paid = 0;
  const balance = Math.max(0, final - paid);
  return { final, paid, balance, status };
};

const getBookingCategoryAmounts = (booking) => {
  if (!booking) return { serviceAmount: 0, labAmount: 0, pharmacyAmount: 0, feesAmount: 0 };
  return {
    serviceAmount: Number(booking.serviceAmount) || 0,
    labAmount: Number(booking.labAmount) || 0,
    pharmacyAmount: Number(booking.pharmacyAmount) || 0,
    feesAmount: Number(booking.feesAmount) || 0,
  };
};

const getReferralPayable = (referrer, booking) => {
  if (!booking) return 0;
  return Number(booking.partnerPayable) || 0;
};

const getCategoryWisePayable = (booking) => {
  if (!booking) return { service: 0, pharmacy: 0, lab: 0, fees: 0 };
  return {
    service: Number(booking.servicePayable) || 0,
    pharmacy: Number(booking.pharmacyPayable) || 0,
    lab: Number(booking.labPayable) || 0,
    fees: Number(booking.feesPayable) || 0,
  };
};

const getReferralToPay = (referrer, booking) => {
  if (!booking) return 0;
  const bookingStatus = (booking.paymentStatus || "Pending") === "Due" ? "Pending" : (booking.paymentStatus || "Pending");
  if (bookingStatus !== "Paid") return null;

  const totalPayable = Number(booking.partnerPayable) || 0;
  const statusKey = referrer?.referralType === "doctor" ? "doctorPaymentStatus" : "customerPaymentStatus";
  const status = booking[statusKey] === "Due" ? "Pending" : (booking[statusKey] || "Pending");
  if (status === "Paid") return 0;
  const paidKey = referrer?.referralType === "doctor" ? "doctorReferralPaidAmount" : "customerReferralPaidAmount";
  const paidAmount = Number(booking[paidKey]) || 0;
  return Math.max(0, Math.round(totalPayable - paidAmount));
};

const getReferralPayoutPct = (booking) => {
  if (!booking) return 0;
  const total = getBookingFinalPayable(booking);
  const payable = Number(booking.partnerPayable) || 0;
  if (total <= 0) return 0;
  return Math.round((payable / total) * 10000) / 100;
};

const getCategoryRateMap = (booking) => {
  return {
    service: { value: Number(booking.serviceCommissionPct) || 0, type: booking.serviceCommissionType || "%" },
    pharmacy: { value: Number(booking.pharmacyCommissionPct) || 0, type: booking.pharmacyCommissionType || "%" },
    lab: { value: Number(booking.labCommissionPct) || 0, type: booking.labCommissionType || "%" },
    fees: { value: Number(booking.feesCommissionPct) || 0, type: booking.feesCommissionType || "%" },
  };
};

const getReferralPayableBreakdown = (referrer, booking) => {
  if (!booking) return { total: 0, items: [] };

  const services = getBookingServices(booking);
  const pctMap = getCategoryRateMap(booking);

  const items = [];
  let total = 0;

  services.forEach((svc) => {
    const price = Number(svc.price) || 0;
    const cat = svc.serviceCategory || "service";
    const meta = pctMap[cat] || { value: 0, type: "%" };
    const payable = meta.type === "₹"
      ? (price > 0 ? meta.value : 0)
      : Math.round((price * meta.value) / 100);
    total += payable;
    items.push({
      name: svc.name,
      price,
      category: cat,
      percent: meta.value,
      percentType: meta.type,
      payable: Math.round(payable),
    });
  });

  const medicineTotal = Number(booking.medicineTotal) || 0;
  if (medicineTotal > 0) {
    const meta = pctMap.pharmacy;
    const payable = meta.type === "₹" ? meta.value : Math.round((medicineTotal * meta.value) / 100);
    total += payable;
    items.push({
      name: "Medicines (Manual)",
      price: medicineTotal,
      category: "pharmacy",
      percent: meta.value,
      percentType: meta.type,
      payable: Math.round(payable),
    });
  }

  const labTotal = Number(booking.labTotal) || 0;
  if (labTotal > 0) {
    const meta = pctMap.lab;
    const payable = meta.type === "₹" ? meta.value : Math.round((labTotal * meta.value) / 100);
    total += payable;
    items.push({
      name: "Lab Tests (Manual)",
      price: labTotal,
      category: "lab",
      percent: meta.value,
      percentType: meta.type,
      payable: Math.round(payable),
    });
  }

  return { total: Math.round(total), items };
};

const formatCommission = (referrer, key, typeKey) => {
  let val = referrer?.[key];
  let type = referrer?.[typeKey];

  if (
    key === "serviceCommission" &&
    (val === 0 || val === undefined || val === null)
  ) {
    if (referrer?.clinicCommission !== undefined && referrer?.clinicCommission !== null) {
      val = referrer.clinicCommission;
      type = referrer.clinicCommissionType || type;
    }
  }

  val = val || 0;
  type = type || "%";
  return type === "₹" ? `₹${val}` : `${val}%`;
};

const formatBookingCommission = (booking, catKey) => {
  if (!booking) return "0%";
  const val = Number(booking[`${catKey}CommissionPct`]) || 0;
  const type = booking[`${catKey}CommissionType`] || "%";
  return type === "₹" ? `₹${val}` : `${val}%`;
};

/* ============================================================
   DateRangePopup — SAME DESIGN AS OpManagement.js
   ============================================================ */
const DateRangePopup = ({
  isOpen, position, fromDate, toDate,
  onFromChange, onToChange, onClear, onClose, dataAttr,
}) => {
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (fromDate) {
        const d = new Date(fromDate);
        if (!isNaN(d.getTime())) setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        else setCalendarMonth(new Date());
      } else {
        setCalendarMonth(new Date());
      }
      setHoverDate(null);
    }
  }, [isOpen, fromDate]);

  if (!isOpen) return null;

  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthDays - i), currentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), currentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), currentMonth: false });
    }
    return days;
  };

  const days = getDaysInMonth(calendarMonth);
  const monthLabel = calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const todayStr = toYMD(new Date());

  const currentStep = !fromDate ? 1 : (!toDate ? 2 : 3);

  const handleDayClick = (dateStr, isCurrentMonth) => {
    if (!isCurrentMonth) return;

    if (!fromDate || (fromDate && toDate)) {
      onFromChange(dateStr);
      onToChange("");
      setHoverDate(null);
    }
    else if (fromDate && !toDate) {
      if (dateStr >= fromDate) {
        onToChange(dateStr);
      } else {
        onFromChange(dateStr);
      }
      setHoverDate(null);
    }
  };

  const handleDayHover = (dateStr, isCurrentMonth) => {
    if (!isCurrentMonth) { setHoverDate(null); return; }
    if (fromDate && !toDate) setHoverDate(dateStr);
  };

  const isInRange = (d) => {
    if (!fromDate) return false;
    if (fromDate && !toDate && hoverDate) {
      const start = fromDate < hoverDate ? fromDate : hoverDate;
      const end = fromDate < hoverDate ? hoverDate : fromDate;
      return d > start && d < end;
    }
    if (!fromDate || !toDate) return false;
    return d > fromDate && d < toDate;
  };

  const isStart = (d) => d === fromDate;
  const isEnd = (d) => d === toDate;
  const isHoverEnd = (d) => fromDate && !toDate && hoverDate === d;

  const fmtDisplay = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-");
    return `${d}/${m}/${y}`;
  };

  const isConfirmEnabled = !!(fromDate && toDate);

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onClose();
  };

  return (
    <div
      data-attr={dataAttr}
      className="fixed bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
      style={{ top: position.top, left: position.left, zIndex: 999999, width: 340 }}
    >
      {/* ─── HEADER ─── */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center gap-2">
          <FaCalendarAlt className="w-3.5 h-3.5 text-blue-600" />
          <h3 className="text-xs font-bold text-gray-900">Select Date Range</h3>
        </div>
        <button
          type="button"
          onClick={() => { onClear(); setHoverDate(null); }}
          className="text-[10px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 px-2 py-1 rounded-md border border-rose-200 transition-colors flex items-center gap-1"
        >
          <FaTimes className="w-2.5 h-2.5" /> Clear
        </button>
      </div>

      {/* ─── STEP INDICATOR ─── */}
      <div className="px-4 pt-2.5 pb-1.5">
        <div className="flex items-center gap-2">
          <div className={`flex-1 flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-all ${currentStep === 1 ? "bg-blue-600 text-white border-blue-600 shadow-sm"
            : fromDate ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-gray-50 text-gray-400 border-gray-200"
            }`}>
            {fromDate ? <FaCheckCircle className="w-2.5 h-2.5" /> : <span className="w-3.5 h-3.5 rounded-full bg-white/30 text-[8px] font-bold flex items-center justify-center">1</span>}
            <span className="text-[9px] font-bold uppercase tracking-wide">From</span>
          </div>
          <div className={`w-5 h-0.5 rounded ${fromDate ? "bg-emerald-400" : "bg-gray-200"}`}></div>
          <div className={`flex-1 flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-all ${currentStep === 2 ? "bg-blue-600 text-white border-blue-600 shadow-sm"
            : toDate ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-gray-50 text-gray-400 border-gray-200"
            }`}>
            {toDate ? <FaCheckCircle className="w-2.5 h-2.5" /> : <span className="w-3.5 h-3.5 rounded-full bg-white/30 text-[8px] font-bold flex items-center justify-center">2</span>}
            <span className="text-[9px] font-bold uppercase tracking-wide">To</span>
          </div>
        </div>
        <p className="text-[9px] text-gray-500 mt-1.5 text-center font-medium">
          {currentStep === 1 && "👆 Select the FROM date"}
          {currentStep === 2 && "👆 Now select the TO date"}
          {currentStep === 3 && "✅ Both dates selected — click Confirm"}
        </p>
      </div>

      {/* ─── FROM / TO DISPLAY BOXES ─── */}
      <div className="px-3 pb-2">
        <div className="grid grid-cols-2 gap-2">
          <div className={`px-2.5 py-1.5 rounded-lg border-2 transition-all ${fromDate ? "border-emerald-400 bg-emerald-50"
            : currentStep === 1 ? "border-blue-400 bg-blue-50 ring-1 ring-blue-500/20"
              : "border-dashed border-gray-300 bg-gray-50"
            }`}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">From</span>
              {fromDate && <FaCheckCircle className="w-2.5 h-2.5 text-emerald-600" />}
            </div>
            <div className={`text-[11px] font-bold ${fromDate ? "text-emerald-800" : "text-gray-400 italic"}`}>
              {fmtDisplay(fromDate) || "Not selected"}
            </div>
          </div>
          <div className={`px-2.5 py-1.5 rounded-lg border-2 transition-all ${toDate ? "border-emerald-400 bg-emerald-50"
            : currentStep === 2 ? "border-blue-400 bg-blue-50 ring-1 ring-blue-500/20"
              : "border-dashed border-gray-300 bg-gray-50"
            }`}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">To</span>
              {toDate && <FaCheckCircle className="w-2.5 h-2.5 text-emerald-600" />}
            </div>
            <div className={`text-[11px] font-bold ${toDate ? "text-emerald-800" : "text-gray-400 italic"}`}>
              {fmtDisplay(toDate) || "Not selected"}
            </div>
          </div>
        </div>
      </div>

      {/* ─── CALENDAR ─── */}
      <div className="px-3 pb-2 pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between mb-1.5">
          <button
            type="button"
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
            className="p-1 hover:bg-gray-100 rounded-md text-gray-600 transition-colors"
          >
            <FaChevronLeft className="w-3 h-3" />
          </button>
          <div className="text-[11px] font-bold text-gray-800">{monthLabel}</div>
          <button
            type="button"
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
            className="p-1 hover:bg-gray-100 rounded-md text-gray-600 transition-colors"
          >
            <FaChevronRight className="w-3 h-3" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {weekdays.map((wd, i) => (
            <div key={wd} className={`text-[8px] font-bold text-center py-0.5 ${i === 0 ? "text-red-500" : "text-gray-500"}`}>{wd}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5" onMouseLeave={() => setHoverDate(null)}>
          {days.map(({ date, currentMonth }, idx) => {
            const ymd = toYMD(date);
            const inRange = isInRange(ymd);
            const start = isStart(ymd);
            const end = isEnd(ymd);
            const hoverEnd = isHoverEnd(ymd);
            const isToday = ymd === todayStr;
            const isWeekend = date.getDay() === 0;

            let cls = "text-[10px] font-semibold py-1.5 rounded-md text-center transition-all relative ";

            if (!currentMonth) {
              cls += "text-gray-300 cursor-default ";
            } else if (start || end) {
              cls += "bg-blue-600 text-white cursor-pointer shadow-sm ";
            } else if (hoverEnd) {
              cls += "bg-blue-400 text-white cursor-pointer shadow-sm ";
            } else if (inRange) {
              cls += "bg-blue-100 text-blue-700 cursor-pointer ";
            } else if (isToday) {
              cls += "bg-amber-50 text-amber-700 ring-1 ring-amber-300 cursor-pointer hover:bg-amber-100 ";
            } else if (isWeekend) {
              cls += "text-red-500 hover:bg-blue-50 cursor-pointer ";
            } else {
              cls += "text-gray-800 hover:bg-blue-50 cursor-pointer ";
            }

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleDayClick(ymd, currentMonth)}
                onMouseEnter={() => handleDayHover(ymd, currentMonth)}
                className={cls}
              >
                {String(date.getDate()).padStart(2, "0")}
                {(start || end) && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 border border-white"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── FOOTER ─── */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50">
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] font-bold text-gray-600 hover:text-gray-900 px-2.5 py-1 rounded-md hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${isConfirmEnabled
            ? "text-white bg-blue-600 hover:bg-blue-700 shadow-sm"
            : "text-gray-400 bg-gray-200 cursor-not-allowed"
            }`}
        >
          <FaCheckCircle className="w-2.5 h-2.5" /> Confirm
        </button>
      </div>
    </div>
  );
};

export default function ReferralBookings() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("customer");

  const [doctorReferrals, setDoctorReferrals] = useState([]);
  const [customerReferrals, setCustomerReferrals] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [referrerFilter, setReferrerFilter] = useState("All");
  const [doctorFilter, setDoctorFilter] = useState("All");
  const [bookingTypeFilter, setBookingTypeFilter] = useState("All");
  const [revenueCategoryFilter, setRevenueCategoryFilter] = useState("All");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [apptFromDate, setApptFromDate] = useState("");
  const [apptToDate, setApptToDate] = useState("");
  const [timeFilter, setTimeFilter] = useState("today");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [activeCardFilter, setActiveCardFilter] = useState("all");
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);

  const [showRegDatePopup, setShowRegDatePopup] = useState(false);
  const [showApptDatePopup, setShowApptDatePopup] = useState(false);
  const [regPopupPos, setRegPopupPos] = useState({ top: 0, left: 0 });
  const [apptPopupPos, setApptPopupPos] = useState({ top: 0, left: 0 });

  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem("referralBookings_itemsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [selectedBookingForModal, setSelectedBookingForModal] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [selectedReferrerForPayment, setSelectedReferrerForPayment] = useState(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [newPaymentMode, setNewPaymentMode] = useState("");
  const [newPaidAmount, setNewPaidAmount] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const refRes = await axios.get(`${REFERRAL_API}/getallreferralcontacts`);
      let allReferrals = [];
      if (refRes.data?.success) allReferrals = refRes.data.data || [];
      else if (Array.isArray(refRes.data)) allReferrals = refRes.data;

      setDoctorReferrals(allReferrals.filter((r) => r.referralType === "doctor"));
      setCustomerReferrals(allReferrals.filter((r) => r.referralType === "customer"));
    } catch (err) {
      console.error("Error fetching referral contacts:", err);
      showToast(err.response?.data?.message || "Failed to load referrals", "error");
    }
    await fetchBookings();
    setLoading(false);
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      
      const today = new Date();
      const toYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      let computedFrom = "";
      let computedTo = "";

      const isAllTime = timeFilter === "All" && !apptFromDate && !apptToDate && !fromDate && !toDate && !selectedMonth;

      if (!isAllTime && !apptFromDate && !apptToDate && timeFilter && timeFilter !== "All") {
        if (timeFilter === "today") {
          computedFrom = toYMD(today); computedTo = toYMD(today);
        } else if (timeFilter === "yesterday") {
          const y = new Date(today); y.setDate(y.getDate() - 1);
          computedFrom = toYMD(y); computedTo = toYMD(y);
        } else if (timeFilter === "last7Days") {
          const s = new Date(today); s.setDate(s.getDate() - 6);
          computedFrom = toYMD(s); computedTo = toYMD(today);
        } else if (timeFilter === "thisWeek") {
          const dow = today.getDay();
          const diff = dow === 0 ? 6 : dow - 1;
          const mon = new Date(today); mon.setDate(mon.getDate() - diff);
          computedFrom = toYMD(mon); computedTo = toYMD(today);
        } else if (timeFilter === "thisMonth") {
          const first = new Date(today.getFullYear(), today.getMonth(), 1);
          const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          computedFrom = toYMD(first); computedTo = toYMD(last);
        } else if (timeFilter === "lastMonth") {
          const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const last = new Date(today.getFullYear(), today.getMonth(), 0);
          computedFrom = toYMD(first); computedTo = toYMD(last);
        } else if (timeFilter === "thisYear") {
          computedFrom = toYMD(new Date(today.getFullYear(), 0, 1));
          computedTo = toYMD(new Date(today.getFullYear(), 11, 31));
        }
      }

      if (isAllTime) {
        params.append("all", "true");
      } else {
        if (computedFrom) params.append("apptFrom", computedFrom);
        if (computedTo) params.append("apptTo", computedTo);
      }

      if (fromDate) params.append("regFrom", fromDate);
      if (toDate) params.append("regTo", toDate);
      if (selectedMonth) params.append("month", selectedMonth);
      if (doctorFilter !== "All") params.append("doctor", doctorFilter);
      if (paymentTypeFilter !== "All") params.append("paymentType", paymentTypeFilter);
      if (statusFilter !== "All") params.append("paymentStatus", statusFilter);
      if (bookingTypeFilter !== "All") params.append("bookingType", bookingTypeFilter);
      if (revenueCategoryFilter !== "All") params.append("revenueCategory", revenueCategoryFilter);
      if (searchQuery.trim()) params.append("search", searchQuery.trim());

      const url = `${BOOKINGS_API}/getallreferralbookings${params.toString() ? "?" + params.toString() : ""}`;
      const res = await axios.get(url);
      
      if (res.data?.success) {
        setBookings(res.data.bookings || []);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error("Error fetching referral bookings:", err);
      setBookings([]);
    }
  };

  useEffect(() => { fetchAllData(); }, []);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeFilter,
    fromDate, toDate, apptFromDate, apptToDate, selectedMonth,
    doctorFilter, paymentTypeFilter, statusFilter,
    bookingTypeFilter, revenueCategoryFilter, searchQuery,
  ]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-attr="reg"]') && !e.target.closest('[data-btn="reg"]')) {
        setShowRegDatePopup(false);
      }
      if (!e.target.closest('[data-attr="appt"]') && !e.target.closest('[data-btn="appt"]')) {
        setShowApptDatePopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeReferrals = activeTab === "doctor" ? doctorReferrals : customerReferrals;
  const isDoctorTab = activeTab === "doctor";

  const getBookingsForReferral = (referral) => {
    if (!referral) return [];
    const refId = String(referral._id || "");
    const refName = (isDoctorTab ? (referral.doctorName || "") : (referral.customerName || "")).trim().toLowerCase();

    return bookings.filter((b) => {
      const cuId = extractId(b.referralCustomerId);
      const dId = extractId(b.referralDoctorId);
      const cId = extractId(b.referralContactId);

      const effectiveId = cuId || dId || cId;
      if (effectiveId) {
        return effectiveId === refId;
      }

      if (!refName) return false;
      const refCustomerName = (extractName(b.referralCustomerId) || "").trim().toLowerCase();
      const refDoctorName = (extractName(b.referralDoctorId) || b.referredByDoctor || "").trim().toLowerCase();
      const referredBy = (b.referredBy || "").trim().toLowerCase();

      return (
        refCustomerName === refName ||
        refDoctorName === refName ||
        referredBy === refName
      );
    });
  };

  const buildFlatRows = () => {
    const rows = [];
    activeReferrals.forEach((ref) => {
      const refBookings = getBookingsForReferral(ref);
      if (refBookings.length === 0) return;
      refBookings.forEach((b) => rows.push({ referrer: ref, booking: b }));
    });

    // ✅ SORTING ADD KI HAI (Latest Booking First)
    rows.sort((a, b) => {
      const dateA = new Date(a.booking.createdAt || a.booking.bookedAt || 0).getTime();
      const dateB = new Date(b.booking.createdAt || b.booking.bookedAt || 0).getTime();
      return dateB - dateA; 
    });

    return rows;
  };

  const flatRows = useMemo(() => buildFlatRows(), [activeReferrals, bookings, activeTab]);

  const uniqueReferrers = useMemo(() => {
    const map = new Map();
    activeReferrals.forEach((r) => {
      const name = isDoctorTab ? r.doctorName : r.customerName;
      if (name) map.set(r._id, name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [activeReferrals, activeTab]);

  const getUniqueDoctors = () => {
    const map = new Map();
    bookings.forEach((b) => { if (b.doctorName) map.set(b.doctorName, true); });
    return Array.from(map.keys());
  };

  const filteredRows = useMemo(() => {
    return flatRows.filter(({ referrer, booking }) => {
      if (!booking) return false;
      if (referrerFilter !== "All" && referrer._id !== referrerFilter) return false;

      if (statusFilter !== "All" && booking.paymentStatus !== statusFilter) return false;

      if (selectedMonth && selectedMonth !== "") {
        const d = new Date(booking.createdAt || booking.bookedAt);
        const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (m !== selectedMonth) return false;
      }

      if (fromDate && toDate) {
        const d = new Date(booking.createdAt || booking.bookedAt);
        const f = new Date(fromDate); f.setHours(0, 0, 0, 0);
        const t = new Date(toDate); t.setHours(23, 59, 59, 999);
        if (d < f || d > t) return false;
      } else if (fromDate) {
        const d = new Date(booking.createdAt || booking.bookedAt);
        const f = new Date(fromDate); f.setHours(0, 0, 0, 0);
        const t = new Date(fromDate); t.setHours(23, 59, 59, 999);
        if (d < f || d > t) return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const name = isDoctorTab ? (referrer.doctorName || "") : (referrer.customerName || "");
        const org = isDoctorTab ? (referrer.doctorOrganization || "") : (referrer.customerAddress || "");
        const phone = isDoctorTab ? (referrer.doctorPhone || "") : (referrer.customerPhone || "");
        const m =
          (booking.patientName || "").toLowerCase().includes(q) ||
          (booking.patientPhone || "").toLowerCase().includes(q) ||
          (booking.customPID || "").toLowerCase().includes(q) ||
          (booking.bookingType || "").toLowerCase().includes(q) ||
          name.toLowerCase().includes(q) ||
          org.toLowerCase().includes(q) ||
          phone.toLowerCase().includes(q);
        if (!m) return false;
      }

      return true;
    });
  }, [flatRows, referrerFilter, statusFilter, searchQuery, fromDate, toDate, selectedMonth, isDoctorTab]);

  useEffect(() => { setCurrentPage(1); }, [
    searchQuery, statusFilter, referrerFilter, fromDate, toDate, selectedMonth, activeTab,
    timeFilter, doctorFilter, bookingTypeFilter, revenueCategoryFilter, paymentTypeFilter,
  ]);

  const stats = useMemo(() => {
    const bookingRows = filteredRows.filter((r) => r.booking);
    const totalBookings = bookingRows.length;

    let paidCount = 0, partialCount = 0, pendingCount = 0;
    bookingRows.forEach(({ booking }) => {
      const raw = booking.paymentStatus || "Pending";
      const st = raw === "Due" ? "Pending" : raw;
      if (st === "Paid") paidCount++;
      else if (st === "Partial") partialCount++;
      else if (st === "Pending") pendingCount++;
    });

    const referrerIds = new Set(bookingRows.map((r) => r.referrer._id));

    return {
      totalReferrers: referrerIds.size,
      totalBookings,
      paidCount,
      partialCount,
      pendingCount,
    };
  }, [filteredRows]);

  const categoryRevenue = useMemo(() => {
    const bookingRows = filteredRows.filter((r) => r.booking);

    const service = { total: 0, cash: 0, online: 0, wallet: 0, pending: 0, footFall: 0, referralPayable: 0 };
    const lab = { total: 0, cash: 0, online: 0, wallet: 0, pending: 0, footFall: 0, referralPayable: 0 };
    const pharmacy = { total: 0, cash: 0, online: 0, wallet: 0, pending: 0, footFall: 0, referralPayable: 0 };
    const fees = { total: 0, cash: 0, online: 0, wallet: 0, pending: 0, footFall: 0, referralPayable: 0 };

    bookingRows.forEach(({ booking }) => {
      const serviceAmt = Number(booking.serviceAmount) || 0;
      const labAmt = Number(booking.labAmount) || 0;
      const pharmacyAmt = Number(booking.pharmacyAmount) || 0;
      const feesAmt = Number(booking.feesAmount) || 0;
      const servicePay = Number(booking.servicePayable) || 0;
      const labPay = Number(booking.labPayable) || 0;
      const pharmacyPay = Number(booking.pharmacyPayable) || 0;
      const feesPay = Number(booking.feesPayable) || 0;

      const paid = Number(booking.amountPaid) || 0;
      const pendingAmt = Number(booking.balanceAmount) || Math.max(0, getBookingFinalPayable(booking) - paid);
      const pt = (booking.paymentType || "").toString().toLowerCase();

      const catTotal = serviceAmt + labAmt + pharmacyAmt + feesAmt;
      if (catTotal <= 0) return;

      const sShare = serviceAmt / catTotal;
      const lShare = labAmt / catTotal;
      const pShare = pharmacyAmt / catTotal;
      const fShare = feesAmt / catTotal;

      if (serviceAmt > 0) service.footFall += 1;
      if (labAmt > 0) lab.footFall += 1;
      if (pharmacyAmt > 0) pharmacy.footFall += 1;
      if (feesAmt > 0) fees.footFall += 1;

      service.total += serviceAmt;
      service.pending += pendingAmt * sShare;
      if (pt === "cash") service.cash += paid * sShare;
      else if (pt === "online") service.online += paid * sShare;
      else if (pt === "wallet") service.wallet += paid * sShare;

      lab.total += labAmt;
      lab.pending += pendingAmt * lShare;
      if (pt === "cash") lab.cash += paid * lShare;
      else if (pt === "online") lab.online += paid * lShare;
      else if (pt === "wallet") lab.wallet += paid * lShare;

      pharmacy.total += pharmacyAmt;
      pharmacy.pending += pendingAmt * pShare;
      if (pt === "cash") pharmacy.cash += paid * pShare;
      else if (pt === "online") pharmacy.online += paid * pShare;
      else if (pt === "wallet") pharmacy.wallet += paid * pShare;

      fees.total += feesAmt;
      fees.pending += pendingAmt * fShare;
      if (pt === "cash") fees.cash += paid * fShare;
      else if (pt === "online") fees.online += paid * fShare;
      else if (pt === "wallet") fees.wallet += paid * fShare;

      service.referralPayable += servicePay;
      lab.referralPayable += labPay;
      pharmacy.referralPayable += pharmacyPay;
      fees.referralPayable += feesPay;
    });

    [service, lab, pharmacy, fees].forEach((obj) => {
      Object.keys(obj).forEach((k) => {
        if (k === "footFall") obj[k] = Number(obj[k]) || 0;
        else obj[k] = Math.round(obj[k]);
      });
    });

    const grandTotal = service.total + lab.total + pharmacy.total + fees.total;
    const grandFootFall = service.footFall + lab.footFall + pharmacy.footFall + fees.footFall;
    const grandReferralPayable =
      service.referralPayable + lab.referralPayable + pharmacy.referralPayable + fees.referralPayable;

    const referralPayoutPct = grandTotal > 0
      ? Math.round((grandReferralPayable / grandTotal) * 10000) / 100
      : 0;

    return {
      service, lab, pharmacy, fees,
      grandTotal,
      grandFootFall,
      grandReferralPayable,
      referralPayoutPct,
    };
  }, [filteredRows]);

  const handleCardClick = (type) => {
    setActiveCardFilter(type);
    setCurrentPage(1);
    if (type === "all") setStatusFilter("All");
    else setStatusFilter(type);
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    // ✅ HAMESHA saari date states clear karo — "All" ho ya na ho
    setApptFromDate("");
    setApptToDate("");
    setSelectedMonth("");
    setFromDate("");
    setToDate("");
  };

  const clearFilters = () => {
    setSearchQuery(""); setStatusFilter("All"); setReferrerFilter("All");
    setFromDate(""); setToDate(""); setSelectedMonth("");
    setApptFromDate(""); setApptToDate("");
    setDoctorFilter("All"); setBookingTypeFilter("All");
    setRevenueCategoryFilter("All"); setPaymentTypeFilter("All");
    setTimeFilter("All");
    setActiveCardFilter("all"); setCurrentPage(1);
    setShowRegDatePopup(false); setShowApptDatePopup(false);
    if (window.innerWidth < 1024) setShowMobileFilters(false);
  };

  const hasActiveFilters =
    searchQuery !== "" || statusFilter !== "All" || referrerFilter !== "All" ||
    fromDate !== "" || toDate !== "" || (selectedMonth && selectedMonth !== "") ||
    doctorFilter !== "All" || bookingTypeFilter !== "All" ||
    revenueCategoryFilter !== "All" || paymentTypeFilter !== "All" ||
    apptFromDate !== "" || apptToDate !== "" || timeFilter !== "All";

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstItem, indexOfLastItem);

  const handleItemsPerPageChange = (e) => {
    const v = Number(e.target.value);
    setItemsPerPage(v);
    localStorage.setItem("referralBookings_itemsPerPage", String(v));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) pages.push(i);
      else if (i === currentPage - 3 || i === currentPage + 3) pages.push("...");
    }
    return pages;
  };

  const downloadCSV = () => {
    if (!filteredRows.length) { alert("No records available to export!"); return; }
    const referrerLabel = isDoctorTab ? "Doctor" : "Customer";
    const headers = ["#", "PID", "Created At", "Booking Type", "Appt. Date", "Slot", `${referrerLabel} Name`, `${referrerLabel} Phone`,
      isDoctorTab ? "Organization" : "Address",
      "Service %", "Pharmacy %", "Lab %", "Fees %", "Patient",
      "Service Amount", "Pharmacy Amount", "Lab Amount", "Fees Amount", "Total",
      "Payment Status", "Payment Mode",
      `${referrerLabel} Payable`, "Referral Paid", "To Pay",
      `${referrerLabel} Payment Status`, "Paid At", "Referral Payout %"];
    const csvRows = [headers.join(","), ...filteredRows.map((row, idx) => {
      const { referrer, booking } = row;
      const name = isDoctorTab ? (referrer.doctorName || "") : (referrer.customerName || "");
      const phone = isDoctorTab ? (referrer.doctorPhone || "") : (referrer.customerPhone || "");
      const extra = isDoctorTab ? (referrer.doctorOrganization || "") : (referrer.customerAddress || "");
      const payStatus = isDoctorTab ? (booking?.doctorPaymentStatus || "Pending") : (booking?.customerPaymentStatus || "Pending");
      const payAt = isDoctorTab ? booking?.doctorPaymentUpdatedAt : booking?.customerPaymentUpdatedAt;
      const paidAmount = isDoctorTab
        ? (booking?.doctorReferralPaidAmount || 0)
        : (booking?.customerReferralPaidAmount || 0);

      const serviceCom = formatBookingCommission(booking, "service");
      const pharmacyCom = formatBookingCommission(booking, "pharmacy");
      const labCom = formatBookingCommission(booking, "lab");
      const feesCom = formatBookingCommission(booking, "fees");

      if (!booking) return [];
      const info = getBookingPaidInfo(booking);
      const payable = getReferralPayable(referrer, booking);
      const toPayRaw = getReferralToPay(referrer, booking);
      const toPay = toPayRaw === null ? "1st Clear Payment" : toPayRaw;
      const cats = getBookingCategoryAmounts(booking);
      return [
        idx + 1,
        `"${booking.customPID || ""}"`,
        `"${formatDateTimeToDDMMYYYY(booking.createdAt)}"`,
        `"${booking.bookingType || ""}"`,
        `"${formatDateToDDMMYYYY(booking.appointmentDate || booking.date)}"`,
        `"${getSlotTiming(booking)}"`,
        `"${name.replace(/"/g, '""')}"`, `"${phone}"`, `"${extra.replace(/"/g, '""')}"`,
        `"${serviceCom}"`, `"${pharmacyCom}"`, `"${labCom}"`, `"${feesCom}"`,
        `"${(booking.patientTitle || "")} ${(booking.patientName || "").replace(/"/g, '""')}"`,
        cats.serviceAmount, cats.pharmacyAmount, cats.labAmount, cats.feesAmount, info.final,
        `"${booking.paymentStatus || "Pending"}"`, `"${booking.paymentType || "cash"}"`,
        payable, paidAmount, `"${toPay}"`, `"${payStatus}"`,
        `"${payAt ? formatDateTimeToDDMMYYYY(payAt) : "-"}"`,
        `"${getReferralPayoutPct(booking)}%"`
      ].join(",");
    })];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${referrerLabel}_Referral_OP_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${filteredRows.length} records!`);
  };

  // ✅ Initialize payment modal with current booking data
  const openPaymentModal = (referrer, booking) => {
    if (!booking) { showToast("No booking selected", "error"); return; }
    setSelectedReferrerForPayment(referrer);
    setSelectedBookingForPayment(booking);

    const totalPayable = getReferralPayable(referrer, booking);
    
    // ✅ Dono default empty rahenge
    setNewPaymentMode(""); 
    setNewPaymentStatus(""); 

    const currentPaymentStatusRaw = isDoctorTab
      ? (booking.doctorPaymentStatus || "Pending")
      : (booking.customerPaymentStatus || "Pending");
    const currentPaymentStatus = currentPaymentStatusRaw === "Due" ? "Pending" : currentPaymentStatusRaw;
    const currentPaidAmount = isDoctorTab
      ? (booking.doctorReferralPaidAmount || 0)
      : (booking.customerReferralPaidAmount || 0);

    let initialAmount = currentPaidAmount;
    if (currentPaymentStatus === "Paid") {
      initialAmount = totalPayable;
    }

    setNewPaidAmount(initialAmount > 0 ? String(initialAmount) : "");

    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedReferrerForPayment(null);
    setSelectedBookingForPayment(null);
    setNewPaymentStatus("");
    setNewPaymentMode("");
    setNewPaidAmount("");
  };

  const handleSavePaymentStatus = async () => {
    if (!selectedBookingForPayment) { showToast("No booking selected", "error"); return; }

    if (!newPaymentMode) {
      showToast("Please select payment mode", "error");
      return;
    }
    if (!newPaymentStatus) {
      showToast("Please select payment status", "error");
      return;
    }

    setSavingPayment(true);
    try {
      const endpoint = isDoctorTab
        ? `${BOOKINGS_API}/updatedoctorpayment/${selectedBookingForPayment._id}`
        : `${BOOKINGS_API}/updatecustomerpayment/${selectedBookingForPayment._id}`;

      const paidAmount = Number(newPaidAmount) || 0;
      const payload = isDoctorTab
        ? {
          doctorPaymentStatus: newPaymentStatus,
          doctorReferralPaidAmount: paidAmount,
          paymentType: newPaymentMode,
        }
        : {
          customerPaymentStatus: newPaymentStatus,
          customerReferralPaidAmount: paidAmount,
          paymentType: newPaymentMode,
        };

      const res = await axios.put(endpoint, payload);
      if (res.data?.success || res.status === 200) {
        const updatedBooking = res.data?.data;

        setBookings((prev) => prev.map((b) => {
          if (b._id !== selectedBookingForPayment._id) return b;

          if (updatedBooking && updatedBooking._id) {
            return { ...b, ...updatedBooking };
          }

          return {
            ...b,
            paymentType: newPaymentMode,
            ...(isDoctorTab
              ? {
                doctorPaymentStatus: newPaymentStatus,
                doctorReferralPaidAmount: paidAmount,
                doctorPaymentUpdatedAt: new Date().toISOString(),
              }
              : {
                customerPaymentStatus: newPaymentStatus,
                customerReferralPaidAmount: paidAmount,
                customerPaymentUpdatedAt: new Date().toISOString(),
              }),
          };
        }));

        const label = isDoctorTab ? "Doctor" : "Customer";
        showToast(`✅ ${label} payment updated (${newPaymentStatus} • ${newPaymentMode})!`, "success");
        closePaymentModal();
      } else {
        showToast(res.data?.message || "Failed to update", "error");
      }
    } catch (err) {
      console.error("❌ Payment update error:", err);
      showToast(err.response?.data?.message || "Failed to update payment", "error");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setReferrerFilter("All");
    setCurrentPage(1);
    setActiveCardFilter("all");
    setStatusFilter("All");
  };

  const referrerLabel = isDoctorTab ? "Doctor" : "Customer";
  const referrerLabelPlural = isDoctorTab ? "Doctors" : "Customers";
  const avatarBg = isDoctorTab ? "bg-purple-500" : "bg-indigo-500";
  const avatarText = isDoctorTab ? "text-purple-800" : "text-indigo-800";
  const payableTextColor = isDoctorTab ? "text-purple-700" : "text-indigo-700";
  const payableBgColor = isDoctorTab ? "bg-purple-50" : "bg-indigo-50";
  const payableBorderColor = isDoctorTab ? "border-purple-200" : "border-indigo-200";
  const statPayableColor = isDoctorTab ? "text-purple-700" : "text-indigo-700";
  const headerAccent = isDoctorTab ? "text-purple-600" : "text-indigo-600";

  const fmt = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

  const CategoryPayableList = ({ booking }) => {
    const payable = getCategoryWisePayable(booking);
    const rateMap = getCategoryRateMap(booking);
    const baseAmounts = getBookingCategoryAmounts(booking);

    const baseKeyMap = {
      service: "serviceAmount",
      pharmacy: "pharmacyAmount",
      lab: "labAmount",
      fees: "feesAmount",
    };

    const categories = ["service", "pharmacy", "lab", "fees"];
    const activeCategories = categories.filter((c) => {
      const base = Number(baseAmounts[baseKeyMap[c]]) || 0;
      return base > 0;
    });

    if (activeCategories.length === 0) {
      return <span className="text-[10px] text-gray-400 italic">—</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        {activeCategories.map((cat) => {
          const meta = CATEGORY_META[cat];
          const Icon = meta.icon;
          const rate = rateMap[cat];
          const base = Number(baseAmounts[baseKeyMap[cat]]) || 0;
          const pay = Number(payable[cat]) || 0;
          return (
            <div
              key={cat}
              className={`flex items-center justify-between gap-2 px-2 py-0.5 rounded border ${meta.bg} ${meta.border} text-[10px] ${meta.text}`}
            >
              <span className="font-semibold whitespace-nowrap flex items-center gap-1">
                <Icon className="text-[9px]" /> {meta.label}:
                <span className="text-[9px] opacity-75 font-normal">
                  ({rate.type === "₹" ? `₹${rate.value}` : `${rate.value}%`})
                </span>
              </span>
              <span className="font-bold whitespace-nowrap">
                <span className="text-[9px] opacity-70 font-normal">₹{base} → </span>
                ₹{pay}
              </span>
            </div>
          );
        })}
        <div className={`flex items-center justify-between gap-2 px-2 py-0.5 rounded border-2 ${payableBorderColor} ${payableBgColor}`}>
          <span className={`text-[10px] font-bold uppercase ${payableTextColor}`}>Total:</span>
          <span className={`text-[11px] font-extrabold ${statPayableColor}`}>
            ₹{Number(booking.partnerPayable) || 0}
          </span>
        </div>
      </div>
    );
  };

  const ReferrerTotalBreakdown = ({ referrer }) => {
    const refBookings = getBookingsForReferral(referrer);
    let totalService = 0, totalPharmacy = 0, totalLab = 0, totalFees = 0;
    refBookings.forEach((b) => {
      totalService += Number(b.servicePayable) || 0;
      totalPharmacy += Number(b.pharmacyPayable) || 0;
      totalLab += Number(b.labPayable) || 0;
      totalFees += Number(b.feesPayable) || 0;
    });
    const grand = totalService + totalPharmacy + totalLab + totalFees;

    const rateMap = {
      service: { value: Number(referrer.serviceCommission ?? referrer.clinicCommission) || 0, type: referrer.serviceCommissionType || referrer.clinicCommissionType || "%" },
      pharmacy: { value: Number(referrer.pharmacyCommission) || 0, type: referrer.pharmacyCommissionType || "%" },
      lab: { value: Number(referrer.labCommission) || 0, type: referrer.labCommissionType || "%" },
      fees: { value: Number(referrer.feesCommission) || 0, type: referrer.feesCommissionType || "%" },
    };

    const values = {
      service: totalService,
      pharmacy: totalPharmacy,
      lab: totalLab,
      fees: totalFees,
    };

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {["service", "pharmacy", "lab", "fees"].map((cat) => {
            const meta = CATEGORY_META[cat];
            const Icon = meta.icon;
            const rate = rateMap[cat];
            return (
              <div key={cat} className={`${meta.bg} p-3 rounded-lg border ${meta.border}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-bold ${meta.text} uppercase flex items-center gap-1`}>
                    <Icon className="text-[10px]" /> {meta.label}
                  </span>
                  <span className={`text-[9px] font-bold ${meta.text} bg-white px-1.5 py-0.5 rounded border ${meta.border}`}>
                    {rate.type === "₹" ? `₹${rate.value}` : `${rate.value}%`}
                  </span>
                </div>
                <div className={`text-lg font-extrabold ${meta.text}`}>
                  ₹{Math.round(values[cat]).toLocaleString("en-IN")}
                </div>
              </div>
            );
          })}
        </div>

        <div className={`p-3 rounded-lg border-2 ${payableBorderColor} ${payableBgColor} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <FiAward className={`w-4 h-4 ${statPayableColor}`} />
            <span className={`text-xs font-bold uppercase ${payableTextColor}`}>
              Total {referrerLabel} Payable
            </span>
          </div>
          <span className={`text-2xl font-extrabold ${statPayableColor}`}>
            ₹{Math.round(grand).toLocaleString("en-IN")}
          </span>
        </div>

        {(() => {
          let totalPaid = 0;
          refBookings.forEach((b) => {
            totalPaid += isDoctorTab
              ? Number(b.doctorReferralPaidAmount) || 0
              : Number(b.customerReferralPaidAmount) || 0;
          });
          const totalToPay = Math.max(0, grand - totalPaid);
          const isGrandZero = grand <= 0;
          return (
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-emerald-50 p-2 rounded-lg border border-emerald-200 text-center">
                <div className="text-[9px] font-bold text-emerald-600 uppercase">Already Paid</div>
                <div className="text-base font-extrabold text-emerald-800">
                  ₹{Math.round(totalPaid).toLocaleString("en-IN")}
                </div>
              </div>
              <div className={`p-2 rounded-lg border text-center ${isGrandZero ? "bg-indigo-50 border-indigo-200" : totalToPay > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                <div className={`text-[9px] font-bold uppercase ${isGrandZero ? "text-indigo-600" : totalToPay > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  Remaining To Pay
                </div>
                <div className={`text-base font-extrabold ${isGrandZero ? "text-indigo-700" : totalToPay > 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {isGrandZero ? `Update ${referrerLabel}` : totalToPay > 0 ? `₹${Math.round(totalToPay).toLocaleString("en-IN")}` : "✓ Settled"}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="emp-dash">
      <main className="p-2 sm:p-4 lg:p-6">
        {toast && (
          <div className={`fixed top-5 right-5 z-[99999] flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white ${toast.type === "error" ? "bg-red-600" : toast.type === "info" ? "bg-cyan-600" : "bg-emerald-600"}`}>
            {toast.type === "error" ? <FiXCircle className="w-5 h-5" /> : <FiCheckCircle className="w-5 h-5" />}
            <span className="font-medium text-sm">{toast.message}</span>
          </div>
        )}

        {/* TABS */}
        <div className="flex items-center gap-2 mb-4 border-b-2 border-gray-200 overflow-x-auto">
          <button onClick={() => handleTabChange("customer")} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${activeTab === "customer" ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <FaUser className="w-4 h-4" /> Customer Referred Bookings
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "customer" ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"}`}>{customerReferrals.length}</span>
          </button>
          <button onClick={() => handleTabChange("doctor")} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all cursor-pointer ${activeTab === "doctor" ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <FaUserMd className="w-4 h-4" /> Doctor Referred Bookings
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "doctor" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"}`}>{doctorReferrals.length}</span>
          </button>
        </div>

        {/* ROW 1 */}
        <div className="hidden lg:grid grid-cols-3 items-center gap-2 mb-3">
          <div className="flex items-center">
            <h1 className="emp-dash__greeting text-lg font-bold whitespace-nowrap">{referrerLabel} Referral <span className={headerAccent}>OP</span></h1>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="relative flex-shrink-0">
              <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input type="text" placeholder="Search PID, Name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-[160px] pl-8 pr-2 py-2 text-xs border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            </div>
            <div className="flex items-center gap-0.5 bg-gray-100 p-1 rounded-lg border border-gray-200">
              {TIME_FILTER_OPTIONS.map((opt) => (
                <button key={opt.value} onClick={() => handleTimeFilterChange(opt.value)}
                  className={`px-2.5 py-1.5 text-[11px] font-bold rounded-md transition-all whitespace-nowrap cursor-pointer ${timeFilter === opt.value ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-white hover:text-gray-900"}`}
                  title={opt.label}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 justify-end">
            <button onClick={downloadCSV} className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 whitespace-nowrap shadow-sm cursor-pointer">
              <FiDownload className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* ROW 2 — Filters */}
        {showRevenueBreakdown && (
          <div className="hidden lg:flex items-center gap-1.5 flex-nowrap mb-3">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg flex-shrink-0 cursor-pointer">
              <option value="All">All Payment</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Pending</option>
            </select>

            <select value={bookingTypeFilter} onChange={(e) => setBookingTypeFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg flex-shrink-0 cursor-pointer">
              {BOOKING_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>

            <select value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg max-w-[110px] truncate flex-shrink-0 cursor-pointer">
              <option value="All">All Doctors</option>
              {getUniqueDoctors().map((d) => <option key={d} value={d}>{d}</option>)}
            </select>

            <select value={revenueCategoryFilter} onChange={(e) => setRevenueCategoryFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg flex-shrink-0 cursor-pointer">
              {REVENUE_CATEGORY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>

            <select value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg flex-shrink-0 cursor-pointer">
              {PAYMENT_TYPE_FILTER_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>

            <select value={referrerFilter} onChange={(e) => setReferrerFilter(e.target.value)} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg max-w-[140px] truncate flex-shrink-0 cursor-pointer">
              <option value="All">All {referrerLabelPlural}</option>
              {uniqueReferrers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>

            {/* ─── REG DATE BUTTON (OpManagement style) ─── */}
            <div className="relative flex-shrink-0">
              <button
                data-btn="reg"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const popupWidth = 340;
                  const left = Math.min(rect.left, window.innerWidth - popupWidth - 20);
                  setRegPopupPos({ top: rect.bottom + 6, left });
                  setShowRegDatePopup(!showRegDatePopup);
                  setShowApptDatePopup(false);
                }}
                className={`flex items-center gap-1.5 h-9 px-2.5 text-xs font-semibold rounded-lg border transition-all whitespace-nowrap cursor-pointer ${(fromDate || toDate) ? "border-blue-500 text-blue-700 bg-blue-50 ring-2 ring-blue-500/10" : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"}`}
              >
                <FaCalendarAlt className="w-3 h-3" />
                <span>
                  {!fromDate && !toDate ? "Reg Date"
                    : fromDate && toDate ? `${fromDate.slice(8, 10)}/${fromDate.slice(5, 7)} – ${toDate.slice(8, 10)}/${toDate.slice(5, 7)}`
                      : fromDate ? `From ${fromDate.slice(8, 10)}/${fromDate.slice(5, 7)}`
                        : `To ${toDate.slice(8, 10)}/${toDate.slice(5, 7)}`}
                </span>
                {(fromDate || toDate) && (
                  <span onClick={(e) => { e.stopPropagation(); setFromDate(""); setToDate(""); }} className="ml-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center hover:bg-red-600 cursor-pointer">✕</span>
                )}
              </button>
              <DateRangePopup
                isOpen={showRegDatePopup}
                position={regPopupPos}
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={setFromDate}
                onToChange={setToDate}
                onClear={() => { setFromDate(""); setToDate(""); }}
                onClose={() => setShowRegDatePopup(false)}
                dataAttr="reg"
              />
            </div>

            {/* ─── APPT DATE BUTTON (OpManagement style) ─── */}
            <div className="relative flex-shrink-0">
              <button
                data-btn="appt"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const popupWidth = 340;
                  const left = Math.min(rect.left, window.innerWidth - popupWidth - 20);
                  setApptPopupPos({ top: rect.bottom + 6, left });
                  setShowApptDatePopup(!showApptDatePopup);
                  setShowRegDatePopup(false);
                }}
                className={`flex items-center gap-1.5 h-9 px-2.5 text-xs font-semibold rounded-lg border transition-all whitespace-nowrap cursor-pointer ${(apptFromDate || apptToDate) ? "border-blue-500 text-blue-700 bg-blue-50 ring-2 ring-blue-500/10" : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"}`}
              >
                <FaCalendarAlt className="w-3 h-3" />
                <span>
                  {!apptFromDate && !apptToDate ? "Appt Date"
                    : apptFromDate && apptToDate ? `${apptFromDate.slice(8, 10)}/${apptFromDate.slice(5, 7)} – ${apptToDate.slice(8, 10)}/${apptToDate.slice(5, 7)}`
                      : apptFromDate ? `From ${apptFromDate.slice(8, 10)}/${apptFromDate.slice(5, 7)}`
                        : `To ${apptToDate.slice(8, 10)}/${apptToDate.slice(5, 7)}`}
                </span>
                {(apptFromDate || apptToDate) && (
                  <span onClick={(e) => { e.stopPropagation(); setApptFromDate(""); setApptToDate(""); }} className="ml-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center hover:bg-red-600 cursor-pointer">✕</span>
                )}
              </button>
              <DateRangePopup
                isOpen={showApptDatePopup}
                position={apptPopupPos}
                fromDate={apptFromDate}
                toDate={apptToDate}
                onFromChange={setApptFromDate}
                onToChange={setApptToDate}
                onClear={() => { setApptFromDate(""); setApptToDate(""); }}
                onClose={() => setShowApptDatePopup(false)}
                dataAttr="appt"
              />
            </div>

            <input type="month" value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setFromDate(""); setToDate(""); setTimeFilter("All"); }} className="h-9 px-2 text-xs border border-gray-300 bg-white rounded-lg w-[110px] flex-shrink-0 cursor-pointer" title="Appointment month" />

            {hasActiveFilters && (
              <button onClick={clearFilters} className="flex items-center gap-1 h-9 px-2.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex-shrink-0 cursor-pointer">
                <FiTrash2 className="w-3 h-3 text-red-500" /> Clear
              </button>
            )}
          </div>
        )}

        {/* MOBILE HEADER */}
        <div className="lg:hidden flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-baseline gap-2">
            <h1 className="text-base font-bold whitespace-nowrap">{referrerLabel} Referral <span className={headerAccent}>OP</span></h1>
            <div className="emp-dash__date-pill text-[10px] px-2 py-1">
              {isDoctorTab ? <FaUserMd className={`w-3 h-3 ${headerAccent}`} /> : <FaUser className={`w-3 h-3 ${headerAccent}`} />}
              <span>{activeReferrals.length}{isDoctorTab ? "D" : "C"} • {stats.totalBookings}B</span>
            </div>
          </div>
          <button onClick={() => setShowMobileFilters(!showMobileFilters)} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer">
            <FiFilter className="w-3 h-3" /> Filters
          </button>
        </div>

        <div className="lg:hidden mb-3">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {TIME_FILTER_OPTIONS.map((opt) => (
              <button key={opt.value} onClick={() => handleTimeFilterChange(opt.value)}
                className={`px-4 py-2.5 text-xs font-bold rounded-lg border transition-all whitespace-nowrap flex-shrink-0 cursor-pointer ${timeFilter === opt.value ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-gray-600 border-gray-300"}`}>{opt.label}</button>
            ))}
          </div>
        </div>

        {showMobileFilters && (
          <div className="lg:hidden mb-4 p-4 bg-white rounded-xl border border-gray-200 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search PID, Name, Phone</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer">
                <option value="All">All Status</option><option value="Partial">Partial</option><option value="Paid">Paid</option><option value="Pending">Pending</option>
              </select>
              <select value={referrerFilter} onChange={(e) => setReferrerFilter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer">
                <option value="All">All {referrerLabelPlural}</option>
                {uniqueReferrers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Booking Type</label>
              <select value={bookingTypeFilter} onChange={(e) => setBookingTypeFilter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer">
                {BOOKING_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={revenueCategoryFilter} onChange={(e) => setRevenueCategoryFilter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer">
                {REVENUE_CATEGORY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <select value={paymentTypeFilter} onChange={(e) => setPaymentTypeFilter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer">
                {PAYMENT_TYPE_FILTER_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Registered Date</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); if (e.target.value) setSelectedMonth(""); }} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
                <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); if (e.target.value) setSelectedMonth(""); }} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Appointment Date</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={apptFromDate} onChange={(e) => { setApptFromDate(e.target.value); if (e.target.value) { setSelectedMonth(""); setTimeFilter("All"); } }} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
                <input type="date" value={apptToDate} onChange={(e) => { setApptToDate(e.target.value); if (e.target.value) { setSelectedMonth(""); setTimeFilter("All"); } }} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
              </div>
            </div>
            <input type="month" value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setFromDate(""); setToDate(""); setTimeFilter("All"); }} className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg cursor-pointer" />
            <div className="pt-3 border-t border-gray-200 flex gap-2">
              <button onClick={downloadCSV} disabled={!filteredRows.length} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg disabled:opacity-50 cursor-pointer"><FiDownload className="w-4 h-4" /> Export</button>
              <button onClick={fetchAllData} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer"><FiRefreshCw className="w-4 h-4" /> Refresh</button>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer">
                <FiTrash2 className="w-4 h-4 text-red-500" /> Clear All
              </button>
            )}
          </div>
        )}

        {/* REVENUE BREAKDOWN */}
        <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <FaRupeeSign className="text-indigo-600 w-5 h-5" />
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Referral Revenue Breakdown</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-indigo-700 bg-white px-3 py-1 rounded-full border border-indigo-200">
                {filteredRows.filter((r) => r.booking).length} bookings
              </span>
              <button
                onClick={() => setShowRevenueBreakdown(!showRevenueBreakdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 shadow-sm transition-colors cursor-pointer"
              >
                {showRevenueBreakdown ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
                {showRevenueBreakdown ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {showRevenueBreakdown && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 p-3">
              {/* SERVICE */}
              <div className="rounded-lg p-4 border border-blue-200 bg-blue-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700 uppercase">
                    <FaServicestack className="text-xs" /> Service Revenue
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-blue-600 leading-tight">FootFall: {categoryRevenue.service.footFall}</span>
                    <span className="text-xl font-extrabold text-blue-800">{fmt(categoryRevenue.service.total)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-emerald-700 whitespace-nowrap">Cash: {fmt(categoryRevenue.service.cash)}</span>
                  <span className="text-cyan-700 whitespace-nowrap">Online: {fmt(categoryRevenue.service.online)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Wallet: {fmt(categoryRevenue.service.wallet)}</span>
                  <span className="text-red-600 whitespace-nowrap">Pending: {fmt(categoryRevenue.service.pending)}</span>
                </div>
              </div>

              {/* LAB */}
              <div className="rounded-lg p-4 border border-purple-200 bg-purple-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-purple-700 uppercase">
                    <FaFlask className="text-xs" /> Lab Revenue
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-purple-600 leading-tight">FootFall: {categoryRevenue.lab.footFall}</span>
                    <span className="text-xl font-extrabold text-purple-800">{fmt(categoryRevenue.lab.total)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-emerald-700 whitespace-nowrap">Cash: {fmt(categoryRevenue.lab.cash)}</span>
                  <span className="text-cyan-700 whitespace-nowrap">Online: {fmt(categoryRevenue.lab.online)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Wallet: {fmt(categoryRevenue.lab.wallet)}</span>
                  <span className="text-red-600 whitespace-nowrap">Pending: {fmt(categoryRevenue.lab.pending)}</span>
                </div>
              </div>

              {/* PHARMACY */}
              <div className="rounded-lg p-4 border border-green-200 bg-green-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-green-700 uppercase">
                    <FaPills className="text-xs" /> Pharmacy Revenue
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-green-600 leading-tight">FootFall: {categoryRevenue.pharmacy.footFall}</span>
                    <span className="text-xl font-extrabold text-green-800">{fmt(categoryRevenue.pharmacy.total)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-emerald-700 whitespace-nowrap">Cash: {fmt(categoryRevenue.pharmacy.cash)}</span>
                  <span className="text-cyan-700 whitespace-nowrap">Online: {fmt(categoryRevenue.pharmacy.online)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Wallet: {fmt(categoryRevenue.pharmacy.wallet)}</span>
                  <span className="text-red-600 whitespace-nowrap">Pending: {fmt(categoryRevenue.pharmacy.pending)}</span>
                </div>
              </div>

              {/* FEES */}
              <div className="rounded-lg p-4 border border-amber-200 bg-amber-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 uppercase">
                    <FaMoneyBillWave className="text-xs" /> Fees Revenue
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-amber-600 leading-tight">FootFall: {categoryRevenue.fees.footFall}</span>
                    <span className="text-xl font-extrabold text-amber-800">{fmt(categoryRevenue.fees.total)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-emerald-700 whitespace-nowrap">Cash: {fmt(categoryRevenue.fees.cash)}</span>
                  <span className="text-cyan-700 whitespace-nowrap">Online: {fmt(categoryRevenue.fees.online)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Wallet: {fmt(categoryRevenue.fees.wallet)}</span>
                  <span className="text-red-600 whitespace-nowrap">Pending: {fmt(categoryRevenue.fees.pending)}</span>
                </div>
              </div>

              {/* TOTAL REVENUE */}
              <div className="rounded-lg p-4 border border-slate-300 bg-slate-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 uppercase">
                    <FaRupeeSign className="text-xs" /> Total Revenue
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-slate-600 leading-tight">FootFall: {categoryRevenue.grandFootFall}</span>
                    <span className="text-xl font-extrabold text-slate-900">{fmt(categoryRevenue.grandTotal)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-blue-700 whitespace-nowrap">Svc: {fmt(categoryRevenue.service.total)}</span>
                  <span className="text-purple-700 whitespace-nowrap">Lab: {fmt(categoryRevenue.lab.total)}</span>
                  <span className="text-green-700 whitespace-nowrap">Pharm: {fmt(categoryRevenue.pharmacy.total)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Fees: {fmt(categoryRevenue.fees.total)}</span>
                </div>
              </div>

              {/* TOTAL PAYABLE */}
              <div className="rounded-lg p-4 border border-rose-300 bg-rose-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-rose-700 uppercase">
                    <FaHandHoldingUsd className="text-xs" /> Total Payable
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-rose-600 leading-tight">Referral</span>
                    <span className="text-xl font-extrabold text-rose-800">{fmt(categoryRevenue.grandReferralPayable)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-blue-700 whitespace-nowrap">Svc: {fmt(categoryRevenue.service.referralPayable)}</span>
                  <span className="text-purple-700 whitespace-nowrap">Lab: {fmt(categoryRevenue.lab.referralPayable)}</span>
                  <span className="text-green-700 whitespace-nowrap">Pharm: {fmt(categoryRevenue.pharmacy.referralPayable)}</span>
                  <span className="text-amber-700 whitespace-nowrap">Fees: {fmt(categoryRevenue.fees.referralPayable)}</span>
                </div>
              </div>

              {/* REFERRAL PAYOUT % */}
              <div className="rounded-lg p-4 border border-fuchsia-300 bg-fuchsia-50 min-h-[120px] flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-fuchsia-700 uppercase">
                    <FiAward className="text-xs" /> Referral Payout %
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] font-bold text-fuchsia-600 leading-tight">of Revenue</span>
                    <span className="text-xl font-extrabold text-fuchsia-800">{categoryRevenue.referralPayoutPct}%</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-bold">
                  <span className="text-slate-600 whitespace-nowrap">Booking: {fmt(categoryRevenue.grandTotal)}</span>
                  <span className="text-rose-700 whitespace-nowrap">Payable: {fmt(categoryRevenue.grandReferralPayable)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className={`emp-dash__stat cursor-pointer hover:scale-105 transition-transform ${activeCardFilter === "all" ? "ring-2 ring-blue-500/20 border-blue-400" : ""}`} onClick={() => handleCardClick("all")}>
            <div className="emp-dash__stat-top"><span className="emp-dash__stat-label">Total {referrerLabelPlural}</span><div className="emp-dash__stat-icon emp-dash__stat-icon--rate"><FiUsers /></div></div>
            <div className="emp-dash__stat-value">{stats.totalReferrers}</div>
            <div className="emp-dash__stat-meta">{stats.totalBookings} bookings</div>
          </div>
          <div className={`emp-dash__stat cursor-pointer hover:scale-105 transition-transform ${activeCardFilter === "Paid" ? "ring-2 ring-emerald-500/20 border-emerald-400" : ""}`} onClick={() => handleCardClick("Paid")}>
            <div className="emp-dash__stat-top"><span className="emp-dash__stat-label">Paid</span><div className="emp-dash__stat-icon emp-dash__stat-icon--present"><FiUserCheck /></div></div>
            <div className="emp-dash__stat-value text-emerald-600">{stats.paidCount}</div>
            <div className="emp-dash__stat-meta">completed payments</div>
          </div>
          <div className={`emp-dash__stat cursor-pointer hover:scale-105 transition-transform ${activeCardFilter === "Partial" ? "ring-2 ring-amber-500/20 border-amber-400" : ""}`} onClick={() => handleCardClick("Partial")}>
            <div className="emp-dash__stat-top"><span className="emp-dash__stat-label">Partial</span><div className="emp-dash__stat-icon emp-dash__stat-icon--rate"><FaClock /></div></div>
            <div className="emp-dash__stat-value text-amber-600">{stats.partialCount}</div>
            <div className="emp-dash__stat-meta">partially paid</div>
          </div>
          <div className={`emp-dash__stat cursor-pointer hover:scale-105 transition-transform ${activeCardFilter === "Pending" ? "ring-2 ring-red-500/20 border-red-400" : ""}`} onClick={() => handleCardClick("Pending")}>
            <div className="emp-dash__stat-top"><span className="emp-dash__stat-label">Pending</span><div className="emp-dash__stat-icon emp-dash__stat-icon--late"><FiXCircle /></div></div>
            <div className="emp-dash__stat-value text-red-500">{stats.pendingCount}</div>
            <div className="emp-dash__stat-meta">pending payments</div>
          </div>
        </div>

        {/* TABLE / CARDS */}
        <div className="emp-dash__card">
          {loading ? (
            <div className="py-12 text-center">
              <FiRefreshCw className={`w-8 h-8 ${headerAccent} animate-spin mx-auto mb-3`} />
              <p className="text-sm text-gray-500">Loading {referrerLabel.toLowerCase()} referral OP records...</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="py-12 text-center">
              {isDoctorTab ? <FaUserMd className="w-12 h-12 text-gray-300 mx-auto mb-3" /> : <FaUser className="w-12 h-12 text-gray-300 mx-auto mb-3" />}
              <h3 className="text-base font-bold text-gray-700">No {referrerLabel} Referral OP Records</h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">
                {flatRows.length === 0 ? `No bookings from referred ${referrerLabelPlural.toLowerCase()} yet.` : "No records match your filters."}
              </p>
              {hasActiveFilters && <button onClick={clearFilters} className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer">Clear Filters</button>}
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="emp-dash__table">
                  <thead>
                    <tr>
                      <th style={{ width: "35px", textAlign: "center" }}>#</th>
                      <th style={{ textAlign: "center", minWidth: "90px" }}>PID</th>
                      <th style={{ textAlign: "center", minWidth: "110px" }}>Created At</th>
                      <th>Referred By {referrerLabel}</th>
                      <th>Patient</th>
                      <th style={{ textAlign: "center", minWidth: "140px" }}>Booking / Slot</th>
                      <th style={{ textAlign: "center", minWidth: "130px" }}>Amount</th>
                      <th style={{ textAlign: "center" }}>Total</th>
                      <th style={{ textAlign: "center" }}>Payment Status</th>
                      <th style={{ textAlign: "center", minWidth: "180px" }}>{referrerLabel} Payable</th>
                      <th style={{ textAlign: "center" }}>To Pay</th>
                      <th style={{ textAlign: "center" }}>{referrerLabel} Pay Status</th>
                      <th style={{ textAlign: "center", minWidth: "110px" }}>Paid At</th>
                      <th style={{ textAlign: "center", minWidth: "140px" }}>Referral %</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((row, idx) => {
                      const { referrer, booking } = row;
                      if (!booking) return null;
                      const name = isDoctorTab ? (referrer.doctorName || "N/A") : (referrer.customerName || "N/A");
                      const subInfo = isDoctorTab ? (referrer.doctorOrganization || "-") : (referrer.customerPhone || "-");

                      const info = getBookingPaidInfo(booking);
                      const paymentColors = getPaymentStatusColors(booking.paymentStatus);
                      const payStatusRaw = isDoctorTab ? (booking.doctorPaymentStatus || "Pending") : (booking.customerPaymentStatus || "Pending");
                      const payStatus = payStatusRaw === "Due" ? "Pending" : payStatusRaw;
                      const payColors = getPaymentStatusColors(payStatus);
                      const cats = getBookingCategoryAmounts(booking);
                      const payAt = isDoctorTab ? booking.doctorPaymentUpdatedAt : booking.customerPaymentUpdatedAt;
                      const bookingTypeInfo = getBookingType(booking);
                      const BookingTypeIcon = bookingTypeInfo.icon;
                      const slotTiming = getSlotTiming(booking);

                      const toPay = getReferralToPay(referrer, booking);
                      const isTotalZero = (Number(booking.partnerPayable) || 0) <= 0;

                      return (
                        <tr key={booking._id} className={`hover:${isDoctorTab ? "bg-purple-50/30" : "bg-indigo-50/30"}`}>
                          <td className="px-2 py-3 text-center text-slate-500 text-[11px]">{indexOfFirstItem + idx + 1}</td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {booking.customPID ? (
                              <span className="inline-block text-[10px] font-bold font-mono text-slate-700 bg-slate-100 border border-slate-300 px-2 py-1 rounded">
                                {booking.customPID}
                              </span>
                            ) : (
                              <span className="text-[10px] text-gray-400 italic">—</span>
                            )}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <div className="font-semibold text-[10px] text-slate-700">
                              {formatDateToDDMMYYYY(booking.createdAt)}
                            </div>
                            <div className="text-[9px] text-gray-400">
                              {booking.createdAt
                                ? new Date(booking.createdAt).toLocaleTimeString("en-IN", {
                                  hour: "2-digit", minute: "2-digit", hour12: true,
                                })
                                : ""}
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-8 h-8 rounded-full ${avatarBg} text-white font-bold flex items-center justify-center text-[10px]`}>{name.charAt(0).toUpperCase()}</div>
                              <div>
                                <div className={`font-semibold text-xs ${avatarText} truncate max-w-[120px]`}>{name}</div>
                                <div className="text-[9px] text-gray-400 truncate max-w-[120px]">{subInfo}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="font-semibold text-xs text-slate-800 truncate max-w-[120px]">{booking.patientTitle || ""} {booking.patientName || "N/A"}</div>
                            <div className="text-[9px] text-gray-400">{booking.patientAge || "?"} yrs • {booking.patientGender || "-"}</div>
                            <div className="text-[9px] text-gray-400"><FaPhoneAlt className="inline text-[8px] mr-1" />{booking.patientPhone || ""}</div>
                          </td>

                          <td className="px-3 py-3 text-center" style={{ minWidth: "140px" }}>
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${bookingTypeInfo.color}`}>
                                <BookingTypeIcon className="w-2.5 h-2.5" /> {bookingTypeInfo.label}
                              </span>
                              <div className="text-[10px] font-semibold text-slate-700">{formatDateToDDMMYYYY(booking.appointmentDate || booking.date)}</div>
                              {slotTiming !== "-" && (<div className="text-[9px] text-blue-700 font-semibold">{slotTiming}</div>)}
                            </div>
                          </td>

                          <td className="px-3 py-3" style={{ minWidth: "130px" }}>
                            <div className="flex flex-col gap-1">
                              {Number(cats.serviceAmount) > 0 && (
                                <div className="flex items-center justify-between gap-1 px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 text-[10px]">
                                  <span className="font-semibold whitespace-nowrap flex items-center gap-1"><FaServicestack className="text-[9px]" /> Service:</span>
                                  <span className="font-bold whitespace-nowrap">₹{Math.round(cats.serviceAmount)}</span>
                                </div>
                              )}
                              {Number(cats.pharmacyAmount) > 0 && (
                                <div className="flex items-center justify-between gap-1 px-2 py-0.5 rounded border border-green-200 bg-green-50 text-green-700 text-[10px]">
                                  <span className="font-semibold whitespace-nowrap flex items-center gap-1"><FaPills className="text-[9px]" /> Pharmacy:</span>
                                  <span className="font-bold whitespace-nowrap">₹{Math.round(cats.pharmacyAmount)}</span>
                                </div>
                              )}
                              {Number(cats.labAmount) > 0 && (
                                <div className="flex items-center justify-between gap-1 px-2 py-0.5 rounded border border-purple-200 bg-purple-50 text-purple-700 text-[10px]">
                                  <span className="font-semibold whitespace-nowrap flex items-center gap-1"><FaFlask className="text-[9px]" /> Lab:</span>
                                  <span className="font-bold whitespace-nowrap">₹{Math.round(cats.labAmount)}</span>
                                </div>
                              )}
                              {Number(cats.feesAmount) > 0 && (
                                <div className="flex items-center justify-between gap-1 px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 text-[10px]">
                                  <span className="font-semibold whitespace-nowrap flex items-center gap-1"><FaMoneyBillWave className="text-[9px]" /> Fees:</span>
                                  <span className="font-bold whitespace-nowrap">₹{Math.round(cats.feesAmount)}</span>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap"><span className="text-xs font-bold text-slate-800">₹{Math.round(info.final)}</span></td>
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${paymentColors.bg} ${paymentColors.text} ${paymentColors.border}`}>
                              <paymentColors.icon className={`w-2.5 h-2.5 ${paymentColors.iconColor}`} /> {booking.paymentStatus || "Pending"}
                            </span>
                          </td>

                          <td className="px-3 py-3 text-center">
                            <CategoryPayableList booking={booking} />
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {isTotalZero ? (
                              <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                                First Update {referrerLabel}
                              </span>
                            ) : toPay === null ? (
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                1st Clear Payment
                              </span>
                            ) : toPay > 0 ? (
                              <span className="text-xs font-extrabold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                                ₹{toPay}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                ✓ Settled
                              </span>
                            )}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <button type="button" onClick={() => openPaymentModal(referrer, booking)}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase border cursor-pointer hover:shadow-md transition-all ${payColors.bg} ${payColors.text} ${payColors.border}`}
                              title={`Click to update referral payment status`}>
                              <payColors.icon className={`w-2.5 h-2.5 ${payColors.iconColor}`} /> {payStatus}
                            </button>
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            {payAt ? (
                              <div className="text-[10px] font-semibold text-slate-700">
                                {formatDateToDDMMYYYY(payAt)}
                                <div className="text-[9px] text-gray-400">{new Date(payAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</div>
                              </div>
                            ) : (<span className="text-[10px] text-gray-400 italic">-</span>)}
                          </td>

                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <div className="flex flex-col gap-0.5 items-center">
                              {Number(booking.serviceAmount) > 0 && (
                                <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                  Service: {formatBookingCommission(booking, "service")}
                                </span>
                              )}
                              {Number(booking.pharmacyAmount) > 0 && (
                                <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100">
                                  Pharmacy: {formatBookingCommission(booking, "pharmacy")}
                                </span>
                              )}
                              {Number(booking.labAmount) > 0 && (
                                <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
                                  Lab: {formatBookingCommission(booking, "lab")}
                                </span>
                              )}
                              {Number(booking.feesAmount) > 0 && (
                                <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                                  Fees: {formatBookingCommission(booking, "fees")}
                                </span>
                              )}
                              <span className="text-[9px] font-bold text-fuchsia-600 bg-fuchsia-50 px-1.5 py-0.5 rounded border border-fuchsia-100 mt-0.5">
                                Payout: {getReferralPayoutPct(booking)}%
                              </span>
                            </div>
                          </td>

                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => { setSelectedReferrer(referrer); setSelectedBookingForModal(booking); setShowDetailModal(true); }}
                                className="p-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg cursor-pointer" title="View Details">
                                <FiEye className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => openPaymentModal(referrer, booking)}
                                className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg cursor-pointer" title={`Update Referral Payment`}>
                                <FiEdit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD VIEW */}
              <div className="lg:hidden p-3 space-y-3 bg-gray-50/50">
                {currentRows.map((row) => {
                  const { referrer, booking } = row;
                  if (!booking) return null;
                  const name = isDoctorTab ? (referrer.doctorName || "N/A") : (referrer.customerName || "N/A");
                  const subInfo = isDoctorTab ? (referrer.doctorOrganization || "-") : (referrer.customerPhone || "-");

                  const info = getBookingPaidInfo(booking);
                  const paymentColors = getPaymentStatusColors(booking.paymentStatus);
                  const payStatusRaw = isDoctorTab ? (booking.doctorPaymentStatus || "Pending") : (booking.customerPaymentStatus || "Pending");
                  const payStatus = payStatusRaw === "Due" ? "Pending" : payStatusRaw;
                  const payColors = getPaymentStatusColors(payStatus);
                  const cats = getBookingCategoryAmounts(booking);
                  const payAt = isDoctorTab ? booking.doctorPaymentUpdatedAt : booking.customerPaymentUpdatedAt;
                  const bookingTypeInfo = getBookingType(booking);
                  const BookingTypeIcon = bookingTypeInfo.icon;
                  const slotTiming = getSlotTiming(booking);

                  const toPay = getReferralToPay(referrer, booking);
                  const isTotalZero = (Number(booking.partnerPayable) || 0) <= 0;

                  return (
                    <div key={booking._id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between gap-2 p-3 border-b border-gray-100 bg-gradient-to-r from-blue-50/60 to-indigo-50/60">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-9 h-9 rounded-full ${avatarBg} text-white font-bold flex items-center justify-center text-xs flex-shrink-0`}>{name.charAt(0).toUpperCase()}</div>
                          <div className="min-w-0 flex-1">
                            <div className={`font-bold text-sm ${avatarText} truncate`}>{name}</div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-1 flex-wrap">
                              <FaPhoneAlt className="text-[8px]" /> {subInfo}
                              {booking.customPID && (
                                <span className="text-[9px] font-bold font-mono text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded ml-1">
                                  {booking.customPID}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full uppercase border ${paymentColors.bg} ${paymentColors.text} ${paymentColors.border} flex-shrink-0`}>
                          <paymentColors.icon className={`w-2.5 h-2.5 ${paymentColors.iconColor}`} /> {booking.paymentStatus || "Pending"}
                        </span>
                      </div>

                      <div className="p-3 space-y-2.5">
                        <div className="flex items-center justify-between gap-2 text-[10px] text-gray-500 flex-wrap">
                          <span>
                            <span className="font-semibold text-slate-600">Created:</span>{" "}
                            {formatDateToDDMMYYYY(booking.createdAt)}
                            {booking.createdAt && (
                              <span className="text-[9px] text-gray-400 ml-1">
                                {new Date(booking.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                              </span>
                            )}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-[11px] flex-wrap">
                          <FaUserInjured className="text-gray-400 text-[10px]" />
                          <span className="font-semibold text-slate-700 truncate">{booking.patientTitle || ""} {booking.patientName || "N/A"}</span>
                          <span className="text-[9px] text-gray-400">({booking.patientAge || "?"} yrs • {booking.patientGender || "-"})</span>
                        </div>

                        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${bookingTypeInfo.color}`}>
                            <BookingTypeIcon className="w-2.5 h-2.5" /> {bookingTypeInfo.label}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-semibold text-slate-700">{formatDateToDDMMYYYY(booking.appointmentDate || booking.date)}</span>
                            {slotTiming !== "-" && (<span className="text-[9px] text-blue-700 font-semibold">{slotTiming}</span>)}
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-1.5 pt-2 border-t border-gray-100">
                          <div className="text-center p-1.5 rounded-lg bg-blue-50 border border-blue-200">
                            <div className="text-[8px] font-bold text-blue-600 uppercase">Service</div>
                            <div className="text-[11px] font-extrabold text-blue-800">₹{Math.round(cats.serviceAmount)}</div>
                          </div>
                          <div className="text-center p-1.5 rounded-lg bg-green-50 border border-green-200">
                            <div className="text-[8px] font-bold text-green-600 uppercase">Pharm</div>
                            <div className="text-[11px] font-extrabold text-green-800">₹{Math.round(cats.pharmacyAmount)}</div>
                          </div>
                          <div className="text-center p-1.5 rounded-lg bg-purple-50 border border-purple-200">
                            <div className="text-[8px] font-bold text-purple-600 uppercase">Lab</div>
                            <div className="text-[11px] font-extrabold text-purple-800">₹{Math.round(cats.labAmount)}</div>
                          </div>
                          <div className="text-center p-1.5 rounded-lg bg-amber-50 border border-amber-200">
                            <div className="text-[8px] font-bold text-amber-600 uppercase">Fees</div>
                            <div className="text-[11px] font-extrabold text-amber-800">₹{Math.round(cats.feesAmount)}</div>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-gray-100">
                          <div className="text-center p-2 rounded-lg bg-gray-50 border border-gray-200">
                            <div className="text-[9px] font-bold text-gray-500 uppercase">Total Amount</div>
                            <div className="text-base font-extrabold text-slate-800">₹{Math.round(info.final)}</div>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-gray-100">
                          <div className="text-[9px] font-bold text-gray-500 uppercase mb-1.5">{referrerLabel} Payable</div>
                          <CategoryPayableList booking={booking} />
                        </div>

                        <div className={`text-center p-2 rounded-lg border ${isTotalZero ? "bg-indigo-50 border-indigo-200" : toPay === null ? "bg-amber-50 border-amber-200" : toPay > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                          <div className={`text-[8px] font-bold uppercase ${isTotalZero ? "text-indigo-600" : toPay === null ? "text-amber-600" : toPay > 0 ? "text-red-600" : "text-emerald-600"}`}>To Pay</div>
                          <div className={`text-sm font-extrabold ${isTotalZero ? "text-indigo-700" : toPay === null ? "text-amber-700" : toPay > 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {isTotalZero ? "First Update Customer" : toPay === null ? "1st Clear Payment" : toPay > 0 ? `₹${toPay}` : "✓ Settled"}
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-bold uppercase text-gray-400">{referrerLabel} Pay:</span>
                            <button type="button" onClick={() => openPaymentModal(referrer, booking)}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase border cursor-pointer hover:shadow-md transition-all ${payColors.bg} ${payColors.text} ${payColors.border}`}>
                              <payColors.icon className={`w-2.5 h-2.5 ${payColors.iconColor}`} /> {payStatus}
                            </button>
                          </div>
                          <div className="text-[10px] text-slate-600 font-medium">{payAt ? formatDateToDDMMYYYY(payAt) : "-"}</div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="text-[10px] text-gray-500">{formatDateToDDMMYYYY(booking.createdAt)}</div>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => { setSelectedReferrer(referrer); setSelectedBookingForModal(booking); setShowDetailModal(true); }}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg text-[10px] font-bold cursor-pointer">
                              <FiEye className="w-3.5 h-3.5" /> View
                            </button>
                            <button onClick={() => openPaymentModal(referrer, booking)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-[10px] font-bold cursor-pointer">
                              <FiEdit2 className="w-3.5 h-3.5" /> Pay
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t bg-gray-50/30">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Show</span>
                    <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="p-1 border rounded-md bg-white cursor-pointer">
                      <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Showing <strong>{filteredRows.length === 0 ? 0 : indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredRows.length)}</strong> of <strong>{filteredRows.length}</strong>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                    className={`px-2.5 py-1 text-xs font-semibold border rounded-lg cursor-pointer ${currentPage === 1 ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50"}`}>Prev</button>
                  {getPageNumbers().map((page, i) => (
                    <button key={i} onClick={() => typeof page === "number" && setCurrentPage(page)} disabled={page === "..."}
                      className={`px-3 py-1 text-xs font-semibold border rounded-lg min-w-[32px] cursor-pointer ${page === "..." ? "text-gray-400 border-transparent" : currentPage === page ? "text-white bg-blue-600 border-blue-600" : "text-gray-700 bg-white hover:bg-gray-50"}`}>{page}</button>
                  ))}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0}
                    className={`px-2.5 py-1 text-xs font-semibold border rounded-lg cursor-pointer ${currentPage === totalPages ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50"}`}>Next</button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* DETAIL MODAL */}
        {showDetailModal && selectedReferrer && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between pb-4 border-b">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${isDoctorTab ? "bg-purple-600" : "bg-indigo-600"} text-white flex items-center justify-center`}>
                    {isDoctorTab ? <FaUserMd /> : <FaUser />}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{referrerLabel} Referral Details</h3>
                    <p className="text-xs text-gray-500">ID: {selectedReferrer._id}</p>
                  </div>
                </div>
                <button onClick={() => { setShowDetailModal(false); setSelectedReferrer(null); setSelectedBookingForModal(null); }} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <FaTimes />
                </button>
              </div>

              <div className="my-4 bg-gray-50 p-5 rounded-xl border space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b">
                  <div className={`w-14 h-14 rounded-full ${avatarBg} text-white font-bold text-xl flex items-center justify-center`}>
                    {(isDoctorTab ? selectedReferrer.doctorName : selectedReferrer.customerName)?.charAt(0).toUpperCase() || (isDoctorTab ? "D" : "C")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-base">{isDoctorTab ? selectedReferrer.doctorName : selectedReferrer.customerName}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      {isDoctorTab ? <FaBuilding className="text-[10px]" /> : <FaPhoneAlt className="text-[10px]" />}
                      {isDoctorTab ? (selectedReferrer.doctorOrganization || "N/A") : (selectedReferrer.customerPhone || "N/A")}
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <div className="text-[10px] font-bold uppercase text-gray-400 mb-2 flex items-center gap-1">
                    <FaRupeeSign className="w-3 h-3" /> {referrerLabel} Wise Total Amount Received
                  </div>
                  <ReferrerTotalBreakdown referrer={selectedReferrer} />
                </div>

                {selectedBookingForModal && (() => {
                  const breakdown = getReferralPayableBreakdown(selectedReferrer, selectedBookingForModal);
                  const payStatusRaw = isDoctorTab ? (selectedBookingForModal.doctorPaymentStatus || "Pending") : (selectedBookingForModal.customerPaymentStatus || "Pending");
                  const payStatus = payStatusRaw === "Due" ? "Pending" : payStatusRaw;
                  const payAt = isDoctorTab ? selectedBookingForModal.doctorPaymentUpdatedAt : selectedBookingForModal.customerPaymentUpdatedAt;
                  const toPay = getReferralToPay(selectedReferrer, selectedBookingForModal);
                  const isTotalZero = (Number(selectedBookingForModal.partnerPayable) || 0) <= 0;
                  const bookingTypeInfo = getBookingType(selectedBookingForModal);
                  const slotTiming = getSlotTiming(selectedBookingForModal);
                  return (
                    <div className="pt-3 border-t">
                      <div className="text-[10px] font-bold uppercase text-gray-400 mb-2">Selected Booking Details</div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-1.5 text-xs">
                        {selectedBookingForModal.customPID && (
                          <div className="flex justify-between"><span className="text-gray-500">PID:</span><span className="font-mono font-bold text-slate-700">{selectedBookingForModal.customPID}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-gray-500">Created At:</span><span className="font-semibold text-[10px]">{formatDateTimeToDDMMYYYY(selectedBookingForModal.createdAt)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Patient:</span><span className="font-semibold">{selectedBookingForModal.patientTitle} {selectedBookingForModal.patientName}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Booking Type:</span>
                          <span className={`font-semibold text-[10px] px-2 py-0.5 rounded-full uppercase border ${bookingTypeInfo.color}`}>{bookingTypeInfo.label}</span>
                        </div>
                        <div className="flex justify-between"><span className="text-gray-500">Appt. Date:</span><span className="font-semibold">{formatDateToDDMMYYYY(selectedBookingForModal.appointmentDate || selectedBookingForModal.date)}</span></div>
                        {slotTiming !== "-" && (
                          <div className="flex justify-between"><span className="text-gray-500">Slot:</span><span className="font-semibold text-blue-700">{slotTiming}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-gray-500">Total Amount:</span><span className="font-semibold">₹{Math.round(getBookingFinalPayable(selectedBookingForModal))}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Referral Payout:</span><span className="font-semibold text-fuchsia-700">{getReferralPayoutPct(selectedBookingForModal)}%</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">To Pay:</span>
                          <span className={`font-bold ${isTotalZero ? "text-indigo-600" : toPay === null ? "text-amber-600" : toPay > 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {isTotalZero ? `Update ${referrerLabel}` : toPay === null ? "1st Clear Payment" : toPay > 0 ? `₹${toPay}` : "✓ Settled"}
                          </span>
                        </div>
                        <div className="flex justify-between"><span className="text-gray-500">{referrerLabel} Payment:</span>
                          <span className={`font-semibold text-[10px] px-2 py-0.5 rounded-full uppercase ${getPaymentStatusColors(payStatus).bg} ${getPaymentStatusColors(payStatus).text}`}>{payStatus}</span>
                        </div>
                        {payAt && (<div className="flex justify-between"><span className="text-gray-500">Paid At:</span><span className="font-semibold text-[10px]">{formatDateTimeToDDMMYYYY(payAt)}</span></div>)}
                      </div>

                      <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase text-gray-400 mb-1.5">Service-wise Commission</div>
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left px-2 py-1.5 text-[9px] font-bold text-gray-500 uppercase">Service</th>
                                <th className="text-center px-2 py-1.5 text-[9px] font-bold text-gray-500 uppercase">Category</th>
                                <th className="text-right px-2 py-1.5 text-[9px] font-bold text-gray-500 uppercase">Price</th>
                                <th className="text-center px-2 py-1.5 text-[9px] font-bold text-gray-500 uppercase">Rate</th>
                                <th className="text-right px-2 py-1.5 text-[9px] font-bold text-gray-500 uppercase">Payable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {breakdown.items.map((it, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                  <td className="px-2 py-1.5 font-medium text-gray-700">{it.name}</td>
                                  <td className="px-2 py-1.5 text-center">
                                    <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${it.category === "service" ? "bg-blue-50 text-blue-700" :
                                        it.category === "pharmacy" ? "bg-green-50 text-green-700" :
                                          it.category === "fees" ? "bg-amber-50 text-amber-700" :
                                            "bg-purple-50 text-purple-700"
                                      }`}>{it.category}</span>
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-semibold text-gray-700">₹{it.price}</td>
                                  <td className="px-2 py-1.5 text-center font-semibold text-gray-600">
                                    {it.percentType === "₹" ? `₹${it.percent}` : `${it.percent}%`}
                                  </td>
                                  <td className={`px-2 py-1.5 text-right font-bold ${payableTextColor}`}>₹{it.payable}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className={`${payableBgColor} border-t-2 ${payableBorderColor}`}>
                              <tr>
                                <td colSpan={4} className={`px-2 py-2 text-right font-bold ${payableTextColor} text-[11px]`}>Total {referrerLabel} Payable</td>
                                <td className={`px-2 py-2 text-right font-extrabold ${statPayableColor} text-[12px]`}>₹{breakdown.total}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { const r = selectedReferrer; const b = selectedBookingForModal; setShowDetailModal(false); setSelectedReferrer(null); setSelectedBookingForModal(null); if (b) openPaymentModal(r, b); }}
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 flex items-center gap-1.5 cursor-pointer">
                  <FiEdit2 className="w-3.5 h-3.5" /> Update Referral Payment
                </button>
                <button onClick={() => { setShowDetailModal(false); setSelectedReferrer(null); setSelectedBookingForModal(null); }} className="px-4 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 cursor-pointer">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* PAYMENT MODAL — FIXED: Title changed to Update Referral Payment */}
        {showPaymentModal && selectedReferrerForPayment && selectedBookingForPayment && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${isDoctorTab ? "bg-purple-600" : "bg-indigo-600"} text-white flex items-center justify-center`}>
                    <FaHandHoldingUsd className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">Update Referral Payment</h3>
                    <p className="text-sm text-gray-500">{isDoctorTab ? selectedReferrerForPayment.doctorName : selectedReferrerForPayment.customerName}</p>
                  </div>
                </div>
                <button
                  onClick={closePaymentModal}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div className={`p-4 ${payableBgColor} rounded-xl border ${payableBorderColor}`}>
                  <div className="flex items-center justify-between mb-2">
                                      <div>
                      {/* ✅ Customer/Doctor name added above Payable text and Bold */}
                      <div className="text-sm font-bold text-slate-800 mb-1">
                        {referrerLabel} Name: {isDoctorTab ? selectedReferrerForPayment.doctorName : selectedReferrerForPayment.customerName}
                      </div>
                      <div className={`text-xs ${payableTextColor} font-bold uppercase`}>{referrerLabel} Payable</div>
                      <div className={`text-2xl font-extrabold ${statPayableColor}`}>
                        ₹{getReferralPayable(selectedReferrerForPayment, selectedBookingForPayment)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Patient</div>
                      <div className="text-sm font-semibold text-slate-700 truncate max-w-[200px]">
                        {selectedBookingForPayment.patientTitle} {selectedBookingForPayment.patientName}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/60">
                    <CategoryPayableList booking={selectedBookingForPayment} />
                  </div>
                </div>

                {/* ✅ REORDERED: Amount -> Type -> Status */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                      Payment Need to Pay (₹)
                    </label>
                    <div className="relative">
                      <FaRupeeSign className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="number"
                        value={newPaidAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewPaidAmount(val);
                          
                          // ✅ Status change only if Payment Type is already selected
                          if (newPaymentMode) {
                            const total = getReferralPayable(selectedReferrerForPayment, selectedBookingForPayment);
                            const paid = Number(val) || 0;
                            if (paid <= 0) setNewPaymentStatus("Pending");
                            else if (paid >= total) setNewPaymentStatus("Paid");
                            else setNewPaymentStatus("Partial");
                          }
                        }}
                        placeholder="0"
                        min="0"
                        className="w-full h-11 bg-white border border-gray-300 rounded-lg pl-9 pr-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                    {/* Pending / Settled text below amount */}
                    {(() => {
                      const totalPayable = getReferralPayable(selectedReferrerForPayment, selectedBookingForPayment);
                      const paidAmt = Number(newPaidAmount) || 0;
                      const pending = Math.max(0, totalPayable - paidAmt);
                      const isTotalZero = totalPayable <= 0;
                      return (
                        <div className="flex items-center justify-between mt-2 px-1">
                          <span className="text-[11px] font-semibold text-gray-500">
                            Pending:{" "}
                            <span className={isTotalZero ? "text-indigo-600 font-bold" : pending > 0 ? "text-red-600 font-bold" : "text-emerald-600 font-bold"}>
                              {isTotalZero ? "Update First" : pending > 0 ? `₹${pending}` : "✓ Settled"}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setNewPaidAmount(String(totalPayable));
                              if (newPaymentMode) {
                                setNewPaymentStatus("Paid");
                              }
                            }}
                            className="text-[11px] font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer"
                          >
                            Set Full (₹{totalPayable})
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                      Payment Type
                    </label>
                    <select
                      value={newPaymentMode}
                      onChange={(e) => {
                        const mode = e.target.value;
                        setNewPaymentMode(mode);
                        // ✅ Payment Type select hote hi status calculate karo
                        if (mode && newPaidAmount) {
                          const total = getReferralPayable(selectedReferrerForPayment, selectedBookingForPayment);
                          const paid = Number(newPaidAmount) || 0;
                          if (paid <= 0) setNewPaymentStatus("Pending");
                          else if (paid >= total) setNewPaymentStatus("Paid");
                          else setNewPaymentStatus("Partial");
                        }
                      }}
                      className={`w-full h-11 bg-white border border-gray-300 rounded-lg px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 capitalize cursor-pointer ${newPaymentMode === "" ? "text-gray-400" : "text-slate-800"}`}
                    >
                      <option value="" disabled>Select Payment Type</option>
                      {PAYMENT_MODE_OPTIONS.map((m) => (
                        <option key={m.value} value={m.value} className="capitalize text-slate-800">
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                      Payment Status
                    </label>
                    <select
                      value={newPaymentStatus}
                      onChange={(e) => setNewPaymentStatus(e.target.value)}
                      className={`w-full h-11 bg-white border border-gray-300 rounded-lg px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer ${newPaymentStatus === "" ? "text-gray-400" : "text-slate-800"}`}
                    >
                      <option value="" disabled>Select Payment Status</option>
                      <option value="Pending" className="text-slate-800">Pending</option>
                      <option value="Partial" className="text-slate-800">Partial</option>
                      <option value="Paid" className="text-slate-800">Paid</option>
                    </select>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase text-gray-400">Live Preview:</span>
                    {(() => {
                      const liveStatus = newPaymentStatus;
                      if (!liveStatus) {
                         return <span className="text-[11px] text-gray-400 italic">Not selected</span>;
                      }
                      const colors = getPaymentStatusColors(liveStatus);
                      return (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full uppercase border ${colors.bg} ${colors.text} ${colors.border}`}>
                          <colors.icon className={`w-3 h-3 ${colors.iconColor}`} /> {liveStatus}
                        </span>
                      );
                    })()}
                    {(newPaymentMode) && (
                      <>
                        <span className="text-[10px] text-gray-400">•</span>
                        <span className="text-[11px] font-bold text-slate-700 capitalize">
                          {newPaymentMode}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={closePaymentModal}
                  className="px-5 py-2.5 rounded-lg text-sm font-bold bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePaymentStatus}
                  disabled={savingPayment}
                  className={`px-6 py-2.5 rounded-lg text-sm font-bold ${isDoctorTab ? "bg-purple-600 hover:bg-purple-700" : "bg-indigo-600 hover:bg-indigo-700"} text-white flex items-center gap-2 disabled:opacity-50 cursor-pointer`}
                >
                  {savingPayment ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
                  {savingPayment ? "Saving..." : "Save Payment"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}